import CustomerPickupSwitch from './CustomerPickupSwitch';
import MetrobiEligibility from './MetrobiEligibility';
import { TableCell } from 'src/components/Table';
import { GetLocation, GetLocationCohort } from 'src/graphql/types/delivery';
import AutomatedRoutingSwitch from '../AutomatedRoutingSwitch';
import config from 'src/config';
import MembershipBadge from '../MembershipBadge';
import { Flex } from 'theme-ui';
import DoorDashIcon from '../Icons/DoorDashIcon';
import DeliveryWindow from '../DeliveryWindow';
import { Checkbox } from '@odekoteam/doppio';
import { isDefined } from 'src/utils';
import HighlightedText from '../HighlightedText';
import RoutingCohortTag from '../RoutingCohortTag';
import { useAppSelector } from 'src/hooks/store';
import { routingLocationsSelector } from 'src/state/selectors/locations';
import EnforceItemScanningSwitch from '../EnforceItemScanningSwitch';
import DeliverWithinStoreHoursSwitch from '../DeliverWithinStoreHoursSwitch';
import PrintDeliverySheetSwitch from './PrintDeliverySheetSwitch';
import IncludeFinancialsSwitch from './IncludeFinancialsSwitch';

const cellStyle = { borderBottom: '1px solid #E6E2DA' };

const LocationRow = ({
  location,
  searchValue,
  setSelectedLocations,
  selectedLocations,
  cohorts,
  showMetrobi = false,
  contextId,
}: {
  location: GetLocation;
  searchValue: string;
  selectedLocations: GetLocation[];
  setSelectedLocations: React.Dispatch<React.SetStateAction<GetLocation[]>>;
  cohorts?: GetLocationCohort[];
  showMetrobi?: boolean;
  contextId?: string;
}): JSX.Element => {
  const soApiUrl = `${config().so_api_url}/manage/locations/${location.keyId}`;
  const isDoorDash = location.isDoordash;
  const selectedLocationIndex = selectedLocations.findIndex(
    (selectedLocation) => selectedLocation.id === location.id,
  );
  const isSelected = selectedLocationIndex !== -1;
  const routingLocations = useAppSelector(routingLocationsSelector);

  const locationInState = routingLocations.find(
    (routingLocation) => routingLocation.locationId === location.id,
  );
  const locationCohort = locationInState?.routingCohort
    ? locationInState.routingCohort
    : location.routingCohort;

  const locationServiceTime = locationInState?.serviceTime
    ? locationInState.serviceTime
    : location.serviceTime ?? 5;

  const locationScheduledDeliveryTimeSource =
    locationInState?.scheduledDeliveryTimeSource
      ? locationInState.scheduledDeliveryTimeSource
      : location.scheduledDeliveryTimeSource;

  const locationDeliverWithinStoreHours =
    locationInState?.deliverWithinStoreHours !== undefined
      ? locationInState.deliverWithinStoreHours
      : location.deliverWithinStoreHours;

  return (
    <>
      <TableCell
        data-test-id={`location-checkbox-cell-${location.id}`}
        style={cellStyle}
      >
        <Checkbox
          isChecked={isSelected}
          accessibilityLabel={`Select ${location.businessName}`}
          isDisabled={!isDefined(location.includeInAutomatedRouting)}
          onChange={(value): void => {
            let newSelectedLocations = [];
            if (value) {
              newSelectedLocations = [...selectedLocations, location];
            } else {
              newSelectedLocations = selectedLocations.filter(
                (selectedLocation) => selectedLocation.id !== location.id,
              );
            }
            setSelectedLocations(newSelectedLocations);
          }}
        />
      </TableCell>
      <TableCell
        data-test-id={`location-so-id-cell-${location.id}`}
        style={cellStyle}
      >
        <a href={soApiUrl} target="_blank" rel="noopener noreferrer">
          <HighlightedText text={location.keyId} highlight={searchValue} />
        </a>
      </TableCell>
      <TableCell
        data-test-id={`location-name-cell-${location.id}`}
        style={cellStyle}
      >
        <Flex>
          <MembershipBadge
            membershipTier={location.membershipTier}
            style={{ marginRight: '8px' }}
            testId={location.id}
          />
          {isDoorDash && (
            <DoorDashIcon style={{ marginRight: '8px' }} testId={location.id} />
          )}
          <HighlightedText
            text={location.businessName}
            highlight={searchValue}
          />
        </Flex>
      </TableCell>
      <TableCell
        data-test-id={`location-city-cell-${location.id}`}
        style={cellStyle}
      >
        {location.address.city}
      </TableCell>
      <TableCell
        data-test-id={`location-state-cell-${location.id}`}
        style={cellStyle}
      >
        {location.address.state}
      </TableCell>
      <TableCell
        data-test-id={`location-postalCode-cell-${location.id}`}
        style={cellStyle}
      >
        {location.address.postalCode}
      </TableCell>
      <TableCell
        data-test-id={`location-cohort-cell-${location.id}`}
        style={cellStyle}
      >
        <RoutingCohortTag routingCohort={locationCohort} />
      </TableCell>
      <TableCell
        data-test-id={`location-service-time-${location.id}`}
        style={cellStyle}
      >
        <span
          style={{
            color:
              locationInState?.serviceTime || location.serviceTime
                ? 'inherit'
                : '#9d9d9d',
          }}
        >
          {locationServiceTime} minutes
        </span>
      </TableCell>
      <TableCell
        data-test-id={`location-automated-routing-cell-${location.id}`}
        style={cellStyle}
      >
        <AutomatedRoutingSwitch
          locationId={location.id}
          value={location.includeInAutomatedRouting}
          routingCohort={location.routingCohort}
          cohorts={cohorts}
        />
      </TableCell>
      <TableCell
        data-test-id={`location-enforce-item-scanning-cell-${location.id}`}
        style={cellStyle}
      >
        <EnforceItemScanningSwitch
          locationId={location.id}
          value={location.enforceItemScanning}
        />
      </TableCell>
      <TableCell
        data-test-id={`location-deliver-within-store-hours-cell-${location.id}`}
        style={cellStyle}
      >
        <DeliverWithinStoreHoursSwitch
          locationId={location.id}
          value={locationDeliverWithinStoreHours}
          storeHoursConfirmed={location.storeHoursConfirmed}
        />
      </TableCell>
      <TableCell
        data-test-id={`location-customer-pickup-cell-${location.id}`}
        style={cellStyle}
      >
        <CustomerPickupSwitch location={location} contextId={contextId} />
      </TableCell>
      <TableCell
        data-test-id={`location-print-delivery-sheet-cell-${location.id}`}
        style={cellStyle}
      >
        <PrintDeliverySheetSwitch
          locationId={location.id}
          value={location.printDeliverySheetEnabled}
        />
      </TableCell>
      <TableCell
        data-test-id={`location-include-financials-cell-${location.id}`}
        style={cellStyle}
      >
        <IncludeFinancialsSwitch
          locationId={location.id}
          value={location.includeFinancialsOnDeliverySheet}
          printDeliverySheetEnabled={location.printDeliverySheetEnabled}
        />
      </TableCell>
      {showMetrobi && (
        <TableCell style={cellStyle}>
          <MetrobiEligibility location={location} />
        </TableCell>
      )}
      <TableCell
        data-test-id={`location-store-hours-confirmed-cell-${location.id}`}
        style={cellStyle}
      >
        <span
          sx={{
            variant: 'text.paragraphMdDefault',
          }}
        >
          {location.storeHoursConfirmed ? 'Yes' : 'No'}
        </span>
      </TableCell>
      <TableCell
        data-test-id={`location-delivery-window-cell-${location.id}`}
        style={cellStyle}
      >
        <div>{locationScheduledDeliveryTimeSource}</div>
      </TableCell>
    </>
  );
};

export default LocationRow;
